import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';
import { copyTextToClipboard } from '../../utils/clipboard';
import { copySecretText } from '../../utils/clipboardSecret';
import { MASK } from './vaultItemFields';

/** Single detail row: masked-by-default secrets with reveal + copy buttons. */
export function FieldRow({ row, t }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  // Re-mask when the row identity/value changes (new selection).
  useEffect(() => {
    setRevealed(false);
    setCopied(false);
  }, [row.value]);

  const doCopy = async () => {
    const ok = row.secret
      ? await copySecretText(row.value)
      : await copyTextToClipboard(row.value);
    if (!ok) return;
    setCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  const shown = row.secret && !revealed ? MASK : row.value;
  // A value from a closed set (auth method, engine, protocol) is a state, not
  // something to paste anywhere — copying it only ever copied "Password" (owner,
  // 01.10.2026). Badge rows and `noCopy` rows get no copy button.
  const copyable = !row.badge && !row.noCopy;
  const multiline = row.multiline && (!row.secret || revealed);

  const buttons = (
    <>
      {row.secret && (
        <button
          type="button"
          className="vault-field-btn"
          title={revealed ? t('vault.hide') : t('vault.reveal')}
          aria-label={revealed ? t('vault.hide') : t('vault.reveal')}
          onClick={() => setRevealed((r) => !r)}
        >
          {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      )}
      {copyable && (
        <button
          type="button"
          className={`vault-field-btn${copied ? ' vault-field-btn--ok' : ''}`}
          title={copied ? t('common.copied') : t('common.copy')}
          aria-label={t('common.copy')}
          onClick={doCopy}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      )}
    </>
  );

  if (multiline) {
    // Multiline rows (notes, keys) STACK: a header row carrying the label on
    // the left and the actions top-right, then the text full-width beneath.
    // The old side-by-side layout put the copy button vertically centred
    // against a paragraph and squeezed the text into the leftover column —
    // for prose, the whole row width is the point.
    return (
      <div className="vault-field vault-field--multiline vault-field--stacked">
        <div className="vault-field-head">
          <span className="vault-field-label">{row.label}</span>
          <div className="vault-field-actions">{buttons}</div>
        </div>
        <span
          className={`vault-field-text vault-field-text--multiline${
            row.mono ? ' vault-field-text--mono' : ''
          }`}
        >
          {shown}
        </span>
      </div>
    );
  }

  return (
    <div className="vault-field">
      <span className="vault-field-label">{row.label}</span>
      <div className="vault-field-value">
        <span
          className={`vault-field-text${row.mono ? ' vault-field-text--mono' : ''}${
            row.badge ? ' vault-field-text--badge' : ''
          }`}
        >
          {shown}
        </span>
        {buttons}
      </div>
    </div>
  );
}
